import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { getCurrentUser } from '../api';
import RouteScrollModeManager from './RouteScrollModeManager';

// 页面和布局全部按路由加载，避免访问首页时把白板、作品展示、二维码、
// KaTeX 等不相关功能一起塞进首包。
const BasicLayout = lazy(() => import('../layouts/BasicLayout'));
const LandingPage = lazy(() => import('../pages/landing'));
const AuthPage = lazy(() => import('../pages/auth'));
const RegisterPage = lazy(() => import('../pages/auth/register'));
const ChangePasswordPage = lazy(() => import('../pages/auth/changePassword'));
const ResetPasswordPage = lazy(() => import('../pages/auth/resetPassword'));
const HomePage = lazy(() => import('../pages/home'));
const ClientPage = lazy(() => import('../pages/client'));
const ProfilePage = lazy(() => import('../pages/profile'));
const ShowcasePage = lazy(() => import('../pages/showcase'));
const ShowcaseDetailPage = lazy(() => import('../pages/showcase/detail'));
const MyWorksPage = lazy(() => import('../pages/my-works'));
const PartnershipPage = lazy(() => import('../pages/partnership'));
const BoardPage = lazy(() => import('../pages/board'));
const FeedbackPage = lazy(() => import('../pages/feedback'));
const DownloadPage = lazy(() => import('../pages/download'));

const RouteFallback = () => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '60vh', gap: 16,
    color: '#5f6368', fontSize: 14,
  }}>
    <div style={{
      width: 36, height: 36, border: '3px solid #e8eaed',
      borderTopColor: '#1a73e8', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <span>正在加载页面…</span>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Route protection helper
const ProtectedRoute = ({ children }) => {
  const user = getCurrentUser();
  if (!user) {
    // If no user, redirect to auth page
    return <Navigate to="/auth" />;
  }
  return children;
};

// 已登录用户不应再经过封面或登录页，路由解析时直接进入功能界面。
const RedirectAuthenticated = ({ children }) => {
  const user = getCurrentUser();
  return user ? <Navigate to="/app/home" replace /> : children;
};

// Route configuration
const RootRouteShell = () => (
  <>
    <RouteScrollModeManager />
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  </>
);

const router = createBrowserRouter([
  {
    element: <RootRouteShell />,
    children: [
      {
        path: '/',
        element: <RedirectAuthenticated><LandingPage /></RedirectAuthenticated>
      },
      {
        path: '/auth',
        element: <RedirectAuthenticated><AuthPage /></RedirectAuthenticated>
      },
      {
        path: '/register',
        element: <RegisterPage />
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />
      },
      {
        path: '/change-password',
        element: <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
      },
      {
        path: '/profile',
        element: <ProtectedRoute><ProfilePage /></ProtectedRoute>
      },
      {
        path: '/my-works',
        element: <ProtectedRoute><MyWorksPage /></ProtectedRoute>
      },
      {
        path: '/showcase',
        element: <ShowcasePage />
      },
      {
        path: '/showcase/:id',
        element: <ShowcaseDetailPage />
      },
      {
        path: '/partnership',
        element: <PartnershipPage />
      },
      {
        path: '/feedback',
        element: <FeedbackPage />
      },
      {
        path: '/download',
        element: <DownloadPage />
      },
      {
        path: '/app',
        element: <BasicLayout />,
        children: [
          {
            path: '',
            element: <Navigate to="/app/home" />
          },
          {
            path: 'home',
            element: <HomePage />
          },
          {
            path: 'board',
            element: (
              <ProtectedRoute>
                <BoardPage />
              </ProtectedRoute>
            )
          }
        ]
      },
      {
        path: '/c',
        element: <BasicLayout />,
        children: [
          {
            path: ':slug',
            element: <ClientPage />
          }
        ]
      },
      {
        path: '*',
        element: <Navigate to="/" />
      }
    ]
  }
]);

export default router; 
